import { useMemo } from 'react';

// Overrides for menuPermission keys that don't derive from simple singular/plural
// matching against the login menu (e.g. backend typos like "campaingns").
// Anything not listed here is resolved dynamically against the `menu` list
// from the login response, so new/renamed modules don't need an entry here.
const MODULE_PERMISSION_MAP = {
  campaign: 'campaingns',
  campaigns: 'campaingns',
};

// Treats "job"/"jobs", "candidate"/"candidates", etc. as the same key.
function keysMatch(a, b) {
  return a === b || `${a}s` === b || `${b}s` === a;
}

// Resolves a moduleName to a menuPermission key by matching it against the
// menu entries returned at login (localStorage['menu']), so the mapping stays
// correct even when the moduleName and the permission/menu key differ only by
// pluralization — without hardcoding every module here.
function resolveMenuKey(normalized) {
  try {
    const rawMenu = localStorage.getItem('menu');
    if (!rawMenu) return null;
    const menu = JSON.parse(rawMenu);
    if (!Array.isArray(menu)) return null;

    const match = menu.find((item) => {
      const key = String(item?.key ?? '').trim().toLowerCase();
      return key && keysMatch(normalized, key);
    });
    return match ? String(match.key).trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

function readRawPermission(moduleName) {
  try {
    const raw = localStorage.getItem('menuPermission');
    if (!raw || !moduleName) return {};
    const all = JSON.parse(raw);
    if (!all || typeof all !== 'object') return {};
    const normalized = String(moduleName).trim().toLowerCase();
    const key = MODULE_PERMISSION_MAP[normalized] ?? resolveMenuKey(normalized) ?? normalized;
    const permissionKeys = Object.keys(all);
    const lower = (k) => k.trim().toLowerCase();
    // Exact match, then pluralization-tolerant — against the menu-resolved key
    // AND the original module name.
    //
    // Both fallbacks matter: resolveMenuKey maps to the MENU's spelling, which
    // is not guaranteed to be the PERMISSION's spelling. Timesheet is exactly
    // that case — the menu lists "timesheet" but menuPermission is keyed
    // "timesheets", so the old exact-match lookup found nothing, returned {},
    // and isAllowed(undefined) defaulted to ALLOW. A module explicitly set to
    // view:0 therefore rendered as fully permitted — permissions silently
    // failing open, which is the worst way for them to fail.
    const matchedKey = permissionKeys.find((pk) => lower(pk) === key)
      ?? permissionKeys.find((pk) => keysMatch(key, lower(pk)))
      ?? permissionKeys.find((pk) => keysMatch(normalized, lower(pk)));
    return (matchedKey ? all[matchedKey] : null) ?? {};
  } catch {
    return {};
  }
}

// Permission values come as 1/0 integers from the API.
// Absent key → default allow (no config = unrestricted).
function isAllowed(value) {
  if (value === undefined || value === null) return true;
  return Number(value) !== 0 && value !== false && value !== '0';
}

export function useMenuPermission(moduleName) {
  return useMemo(() => {
    const p = readRawPermission(moduleName);
    return {
      canView:    isAllowed(p.view),
      canAdd:     isAllowed(p.add),
      canEdit:    isAllowed(p.edit),
      canDelete:  isAllowed(p.delete),
      canApprove: isAllowed(p.approve),
      canReject:  isAllowed(p.reject),
    };
  }, [moduleName]);
}
